import { createAction } from 'redux-actions';
import { positionsService } from '../../services';
import { formatData } from '../../utils/transformer.util';

// Action types
export const GET_POSITIONS_REQUEST = 'GET_POSITIONS_REQUEST';
export const GET_POSITIONS_SUCCESS = 'GET_POSITIONS_SUCCESS';
export const GET_POSITIONS_FAILURE = 'GET_POSITIONS_FAILURE';

export const CREATE_POSITION_REQUEST = 'CREATE_POSITION_REQUEST';
export const CREATE_POSITION_SUCCESS = 'CREATE_POSITION_SUCCESS';
export const CREATE_POSITION_FAILURE = 'CREATE_POSITION_FAILURE';

export const UPDATE_POSITION_REQUEST = 'UPDATE_POSITION_REQUEST';
export const UPDATE_POSITION_SUCCESS = 'UPDATE_POSITION_SUCCESS';
export const UPDATE_POSITION_FAILURE = 'UPDATE_POSITION_FAILURE';

export const DELETE_POSITION_REQUEST = 'DELETE_POSITION_REQUEST';
export const DELETE_POSITION_SUCCESS = 'DELETE_POSITION_SUCCESS';
export const DELETE_POSITION_FAILURE = 'DELETE_POSITION_FAILURE';

export const GET_POSITIONS_OPTIONS_REQUEST = 'GET_POSITIONS_OPTIONS_REQUEST';
export const GET_POSITIONS_OPTIONS_SUCCESS = 'GET_POSITIONS_OPTIONS_SUCCESS';
export const GET_POSITIONS_OPTIONS_FAILURE = 'GET_POSITIONS_OPTIONS_FAILURE';

// Action creators
export const getPositionsRequest = createAction(GET_POSITIONS_REQUEST);
export const getPositionsSuccess = createAction(GET_POSITIONS_SUCCESS);
export const getPositionsFailure = createAction(GET_POSITIONS_FAILURE);

export const createPositionRequest = createAction(CREATE_POSITION_REQUEST);
export const createPositionSuccess = createAction(CREATE_POSITION_SUCCESS);
export const createPositionFailure = createAction(CREATE_POSITION_FAILURE);

export const updatePositionRequest = createAction(UPDATE_POSITION_REQUEST);
export const updatePositionSuccess = createAction(UPDATE_POSITION_SUCCESS);
export const updatePositionFailure = createAction(UPDATE_POSITION_FAILURE);

export const deletePositionRequest = createAction(DELETE_POSITION_REQUEST);
export const deletePositionSuccess = createAction(DELETE_POSITION_SUCCESS);
export const deletePositionFailure = createAction(DELETE_POSITION_FAILURE);

export const getPositionsOptionsRequest = createAction(GET_POSITIONS_OPTIONS_REQUEST);
export const getPositionsOptionsSuccess = createAction(GET_POSITIONS_OPTIONS_SUCCESS);
export const getPositionsOptionsFailure = createAction(GET_POSITIONS_OPTIONS_FAILURE);

/**
 * Get all positions
 * @returns {Array} - Array of positions
 */
export const getPositions = () => async (dispatch) => {
  dispatch(getPositionsRequest());

  try {
    const response = await positionsService.getPositions();

    if (!response || !response.data) {
      throw new Error('Invalid response from positions service');
    }

    dispatch(getPositionsSuccess(response.data));
    return response.data;
  } catch (error) {
    console.error('Error fetching positions:', error);
    dispatch(getPositionsFailure(error));
    throw error;
  }
};

/**
 * Create a new position
 * @param {Object} positionData - Position data to create
 * @returns {Object} - Created position
 */
export const createPosition = positionData => async (dispatch) => {
  dispatch(createPositionRequest());

  try {
    const response = await positionsService.createPosition(positionData);

    if (!response || !response.data) {
      throw new Error('Invalid response from positions service');
    }

    dispatch(createPositionSuccess(response.data));
    return response.data;
  } catch (error) {
    console.error('Error creating position:', error);
    dispatch(createPositionFailure(error));
    throw error;
  }
};

/**
 * Update an existing position
 * @param {string|number} id - Position ID
 * @param {Object} positionData - Position data to update
 * @returns {Object} - Updated position
 */
export const updatePosition = (id, positionData) => async (dispatch) => {
  dispatch(updatePositionRequest());

  try {
    const response = await positionsService.updatePosition(id, positionData);

    if (!response || !response.data) {
      throw new Error('Invalid response from positions service');
    }

    dispatch(updatePositionSuccess(response.data));
    return response.data;
  } catch (error) {
    console.error('Error updating position:', error);
    dispatch(updatePositionFailure(error));
    throw error;
  }
};

/**
 * Delete a position
 * @param {string|number} id - Position ID to delete
 * @returns {boolean} - Success status
 */
export const deletePosition = id => async (dispatch) => {
  dispatch(deletePositionRequest());

  try {
    await positionsService.deletePosition(id);
    dispatch(deletePositionSuccess(id));
    return true;
  } catch (error) {
    console.error('Error deleting position:', error);
    dispatch(deletePositionFailure(error));
    throw error;
  }
};

/**
 * Get positions formatted for select components
 * @param {Object} filters - Filter parameters
 * @returns {Array} - Array of position options with label and value properties
 */
export const getPositionsOptions = (filters = {}) => async (dispatch) => {
  dispatch(getPositionsOptionsRequest());

  try {
    const response = await positionsService.getPositionsOptions(filters);

    if (!response || !response.data) {
      throw new Error('Invalid response from positions service');
    }

    // Format the data for select components (label/value pairs)
    const formattedData = formatData(response.data, {
      labelKey: 'name',
      valueKey: 'id',
    });

    dispatch(getPositionsOptionsSuccess(formattedData));
    return formattedData;
  } catch (error) {
    console.error('Error fetching positions options:', error);
    dispatch(getPositionsOptionsFailure(error));
    throw error;
  }
};
